import { useEffect, useRef, type RefObject } from "react"
import { Formik, getIn, useFormikContext } from "formik"
import { CharField, SingleSelectField } from "@inmagik/magik-ui"
import * as yup from "yup"
import type { BlockBase, Media, ImageBlock, MediaFit } from "../../../../types"
import { MediaThumb } from "../Previews"
import { mediaFitOptions, mediaPositionOptions } from "../../options"
import { AutoValidate } from "./Autovalidate"
import { WysiwygField } from "../../../../components/WysiwygField"
import { MediaSelectionModal } from "../MediaSelectionModal"

function SingleMediaPicker() {
  const { values, setFieldValue } = useFormikContext<ImageBlock>()

  const selectMedia = (doc: Media) => {
    setFieldValue("mediaId", doc.id)
    setFieldValue("mediaTitle", doc.title ?? "")
    setFieldValue("mediaThumbnail", String(doc.thumbnail ?? doc.media_file ?? ""))
    setFieldValue("mediaFile", String(doc.media_file ?? ""))
  }

  return (
    <MediaSelectionModal
      buttonLabel={values.mediaId ? "Cambia media" : "Seleziona media"}
      title="Seleziona media"
      selectionSummary={values.mediaId ? "1 immagine selezionata." : ""}
      renderSelectionCell={(doc) => (
        <input type="radio" name="mediaId" checked={values.mediaId === doc.id} onChange={() => selectMedia(doc)} />
      )}
      onCreateAndSelect={selectMedia}
    />
  )
}

export function ImageBlockForm({
  block,
  saveBlock,
  setBlockFormValid,
  submitRef,
}: {
  block: BlockBase
  saveBlock: (b: BlockBase) => void
  setBlockFormValid: (v: boolean) => void
  submitRef: RefObject<HTMLButtonElement | null>
}) {
  const schema = yup.object().shape({
    slideTitle: yup.string().required("Il titolo della slide è obbligatorio"),
    mediaId: yup.number().required("Seleziona un'immagine"),
    caption: yup.string().nullable(),
    fit: yup.string().oneOf(["fit", "cover"]).required("Seleziona fit/cover"),
    position: yup.string().when("fit", {
      is: "cover",
      then: (s) => s.required("Seleziona la posizione dell'immagine"),
      otherwise: (s) => s.nullable(),
    }),
  })

  const hiddenSubmitRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!submitRef?.current) return
    submitRef.current.onclick = () => hiddenSubmitRef.current?.click()
  }, [submitRef])

  const initialValues: ImageBlock = {
    uuid: block.uuid,
    blockType: "Image",
    slideTitle: (block as ImageBlock).slideTitle ?? "",
    mediaId: (block as ImageBlock).mediaId ?? undefined,
    mediaTitle: (block as ImageBlock).mediaTitle ?? "",
    mediaThumbnail: (block as ImageBlock).mediaThumbnail ?? "",
    mediaFile: (block as ImageBlock).mediaFile ?? "",
    caption: (block as ImageBlock).caption ?? "",
    fit: (block as ImageBlock).fit ?? "fit",
    position: (block as ImageBlock).position ?? "center center",
  }

  return (
    <Formik
      enableReinitialize
      validateOnMount
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => saveBlock(values)}
    >
      {({ handleSubmit, values, errors, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <CharField
            label="Titolo slide"
            layout="horizontal"
            value={values.slideTitle}
            onChange={(v) => setFieldValue("slideTitle", v)}
            required
            isValid={!getIn(errors, "slideTitle")}
            errorMessage={getIn(errors, "slideTitle")}
            className="mb-3"
          />

          <div className="d-flex align-items-center flex-column gap-2 mb-3 w-100">
            <MediaThumb
              thumbnail={values.mediaThumbnail}
              title={values.mediaTitle}
              fit={values.fit}
              width={300}
              height={200}
              position={values.position}
            />
            <div className="mb-2">
              <strong>Selezionata:</strong>{" "}
              {values.mediaId ? (
                <>
                  ID {values.mediaId}
                  {values.mediaTitle ? ` — ${values.mediaTitle}` : ""}
                </>
              ) : (
                <span className="text-muted">nessuna</span>
              )}
            </div>
            <div className="mb-3">
              <SingleMediaPicker />
            </div>
          </div>

          <div className="mb-3">
            <div className="mb-2">
              <SingleSelectField
                label="Fit"
                layout="horizontal"
                options={mediaFitOptions}
                value={mediaFitOptions.find((o) => o.value === values.fit)}
                onChange={(opt) => {
                  if (!opt) return
                  setFieldValue("fit", opt.value as MediaFit)

                  if (opt.value === "cover" && !values.position) {
                    setFieldValue("position", "center center")
                  }
                }}
                errorMessage={getIn(errors, "fit")}
              />
            </div>
            {values.fit === "cover" && (
              <div className="mb-2">
                <SingleSelectField
                  label="Posizione immagine"
                  layout="horizontal"
                  options={mediaPositionOptions}
                  value={mediaPositionOptions.find((o) => o.value === values.position)}
                  onChange={(opt) => opt && setFieldValue("position", opt.value)}
                  errorMessage={getIn(errors, "position")}
                />
              </div>
            )}
            <div>
              <WysiwygField
                label="Didascalia immagine"
                layout="horizontal"
                value={values.caption || ""}
                onChange={(v) => setFieldValue("caption", v)}
                isValid={!getIn(errors, "caption")}
                errorMessage={getIn(errors, "caption")}
                className="mb-2"
                placeholder="(opzionale)"
                rows={6}
              />
            </div>
          </div>

          <button ref={hiddenSubmitRef} type="submit" style={{ display: "none" }} />
          <AutoValidate setBlockFormValid={setBlockFormValid} />
        </form>
      )}
    </Formik>
  )
}
